import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AuthService } from "../services";
import {
  getStoredUser,
  getStoredToken,
  getStoredSession,
  setStoredSession,
  clearStoredSession,
  cancelPendingRequests,
  activateStoredSession,
  setAuthFailureHandler,
} from "../api/axiosClient";
import { isJwtExpired } from "../utils/jwt";
import { STORAGE_KEYS } from "../constants/auth";

const AuthContext = createContext(null);

const normalizeRole = (role) => {
  if (!role) return "";
  return role.replace("ROLE_", "").toUpperCase();
};

/**
 * Authentication provider.
 *
 *  - JWT + user are persisted to localStorage so a refresh keeps the session.
 *  - On boot, an expired JWT clears the session BEFORE any protected request
 *    is made.
 *  - A `storage` event listener keeps the auth state in sync across tabs
 *    (logging out in one tab logs the user out in all tabs).
 *  - 401 from the axios response interceptor calls `onAuthFailure` which
 *    drops the session and forces redirect to /login.
 */
export const AuthProvider = ({ children }) => {
  const queryClient = useQueryClient();
  const initial = (() => {
    activateStoredSession();
    const token = getStoredToken();
    const storedUser = getStoredUser();

    // Upgrade the previous two-key session on boot before any routes or API
    // requests can observe it as an active session.
    if (token && storedUser && !localStorage.getItem(STORAGE_KEYS.SESSION)) {
      setStoredSession(token, storedUser);
    }

    if (token && isJwtExpired(token)) {
      clearStoredSession();
      return { token: null, user: null };
    }

    const normalizedUser = storedUser
      ? {
          ...storedUser,
          roleName: normalizeRole(storedUser.roleName || storedUser.role),
        }
      : null;

    return { token, user: normalizedUser };
  })();

  const [user, setUser] = useState(initial.user);
  const [token, setToken] = useState(initial.token);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Mark the shared session active in this tab only after React commits the
  // matching identity. Requests from the old UI are gated until that point.
  useLayoutEffect(() => {
    const session = getStoredSession();
    const matchesContext = session
      ? session.token === token &&
        session.user?.userId === user?.userId &&
        normalizeRole(session.user?.roleName || session.user?.role) === user?.roleName
      : !token && !user;
    if (matchesContext) activateStoredSession(session);
  }, [token, user]);

  const login = useCallback(async (username, password) => {
    setLoading(true);
    setError(null);

    try {
      const res = await AuthService.login(username, password);

      const u = {
        userId: res.userId,
        username: res.username,
        fullName: res.fullName,
        roleName: normalizeRole(res.roleName || res.role),
      };

      queryClient.clear();
      setStoredSession(res.accessToken, u);
      setToken(res.accessToken);
      setUser(u);

      return u;
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Login failed";
      setError(msg);
      throw new Error(msg);
    } finally {
      setLoading(false);
    }
  }, [queryClient]);

  const logout = useCallback(() => {
    queryClient.clear();
    clearStoredSession();
    setToken(null);
    setUser(null);
  }, [queryClient]);

  // 401 from axios → drop the session
  useEffect(() => {
    setAuthFailureHandler(() => {
      queryClient.clear();
      setToken(null);
      setUser(null);
    });
  }, [queryClient]);

  // Cross-tab sync: when another tab logs out / changes user, mirror it here.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEYS.SESSION) {
        cancelPendingRequests();
        queryClient.clear();
        try {
          const session = getStoredSession();
          const parsedUser = session?.user || null;

          const normalizedUser = parsedUser
            ? {
                ...parsedUser,
                roleName: normalizeRole(parsedUser.roleName || parsedUser.role),
              }
            : null;

          setToken(session?.token || null);
          setUser(normalizedUser);
        } catch {
          setToken(null);
          setUser(null);
        }
      }
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [queryClient]);

  // Heart-beat expiry watcher every 60s.
  useEffect(() => {
    if (!token) return;

    const id = setInterval(() => {
      if (isJwtExpired(token)) {
        logout();
      }
    }, 60_000);

    return () => clearInterval(id);
  }, [token, logout]);

  const value = useMemo(
    () => ({
      user,
      token,
      loading,
      error,
      login,
      logout,
      isAuthenticated: !!token && !!user,
      role: user?.roleName,
      isOwner: user?.roleName === "OWNER",
      isAdmin: user?.roleName === "ADMIN",
      isCashier: user?.roleName === "BAR_CASHIER",
      isOwnerOrAdmin: user?.roleName === "OWNER" || user?.roleName === "ADMIN",
    }),
    [user, token, loading, error, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);

  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }

  return ctx;
};
